import React, { useEffect, useState } from 'react';

import LinearProgress from '@material-ui/core/LinearProgress';
import { isEmpty } from 'lodash';
import {
  transcriptionsAndSolutionsCheckHasResults,
  transcriptionsAndSolutonsSendForPreprocessing
} from 'sdk';

import { colors } from 'theme/palette';
import { notifyErrors } from 'utils/notifications';

import EdlightTip from './components/EdlightTip';
import Typography from 'components/Typography';

import styles from './styles.module.scss';

const getRemainingTimeMessage = (seconds) => {
  if (seconds <= 0) {
    return 'Finishing...';
  } else if (seconds <= 60) {
    return `Less than a minute remaining`;
  } else {
    const minutes = Math.ceil(seconds / 60);
    return `About ${minutes} minute${minutes > 1 ? 's' : ''} remaining`;
  }
};
const steps = [
  'Upload received',
  'Locating questions',
  'Extracting text from questions',
  'Identifying answers...',
  'Reviewing for clarity'
];

const PreparingPage = ({ onPrepareSuccess, trackerId, activeStep }) => {
  const maxAttempts = 180;
  const attemptsPerStep = maxAttempts / 5;

  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [remainingTime, setRemainingTime] = useState(maxAttempts);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    setRemainingTime((prevRemainingTime) => {
      const newRemainingTime = prevRemainingTime - 3;
      if (newRemainingTime < 0) return 0;
      return newRemainingTime;
    });
  }, [currentAttempt]);

  useEffect(() => {
    if (currentAttempt <= attemptsPerStep) {
      setCurrentStep(0);
    } else if (currentAttempt <= attemptsPerStep * 2) {
      setCurrentStep(1);
    } else if (currentAttempt <= attemptsPerStep * 3) {
      setCurrentStep(2);
    } else if (currentAttempt <= attemptsPerStep * 4) {
      setCurrentStep(3);
    } else {
      setCurrentStep(4);
    }
  }, [currentAttempt, attemptsPerStep]);

  useEffect(() => {
    const processTranscriptionsAndSolutions = async () => {
      try {
        await transcriptionsAndSolutonsSendForPreprocessing(trackerId);

        const pollCheckTranscriptionsAndSolutionsHasResults = async () => {
          const delay = 3000;

          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            setCurrentAttempt((prevAttempt) => prevAttempt + 1);

            const { data, errors } =
              await transcriptionsAndSolutionsCheckHasResults({ trackerId });

            if (!isEmpty(errors)) {
              notifyErrors(errors);
              throw new Error('Error checking for results.');
            }
            const { is_processing_ready } = data;

            if (is_processing_ready) {
              return;
            }

            await new Promise((resolve) => setTimeout(resolve, delay));
          }

          throw new Error('Timeout.');
        };

        await pollCheckTranscriptionsAndSolutionsHasResults();

        onPrepareSuccess();
      } catch (err) {
        onPrepareSuccess();
      }
    };

    processTranscriptionsAndSolutions();
  }, [trackerId, onPrepareSuccess, activeStep, maxAttempts]);

  return (
    <div className={styles.preparingPage}>
      <div className={styles.loaderContainer}>
        <Typography
          className={styles.loaderText}
          color={colors.blue1}
          variant={'H-TEXT-2'}
        >
          We're preparing your assignment
        </Typography>
      </div>
      <div className={styles.loaderContainer}>
        <Typography
          variant={'B-Text-2'}
          className={styles.subText}
          color={colors.grey2}
        >
          {steps[currentStep]} | {getRemainingTimeMessage(remainingTime)}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min((currentAttempt / maxAttempts) * 100, 100)}
          className={styles.customSpinner}
        />
      </div>
      <EdlightTip />
    </div>
  );
};

export default PreparingPage;
